import { hasMany, Factory, Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { PaymentMethodData } from '@/composables/payments'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import { generateFinanceAmount } from '@@/api/mock-server/utils.js'
import { PaymentMethod, type PaymentMethods } from '@/models/payments.js'
import { rand, randChanceBoolean } from '@ngneat/falso'
import { roundEven, toNumber } from '@/utils/number.js'

export const model = Model.extend({
  payments: hasMany('invoicePayment'),
}) as ModelDefinition<PaymentMethodData>

const paymentMethodNames = {
  [PaymentMethod.CreditCard]: 'Card',
  [PaymentMethod.Cash]: 'Cash',
  [PaymentMethod.Invoice]: 'Bank payment',
  [PaymentMethod.Rounding]: 'Rounding',
  [PaymentMethod.Cheque]: 'Cheque',
  [PaymentMethod.Voucher]: 'Voucher',
  [PaymentMethod.ConsolidatedInvoice]: 'Consolidated invoice',
  [PaymentMethod.Mobile]: 'Mobile',
  [PaymentMethod.Prepayment]: 'Prepayment',
  [PaymentMethod.PresentCard]: 'Present card',
  [PaymentMethod.FinancingCompany]: 'Financing company',
  [PaymentMethod.Other]: 'Other',
  [PaymentMethod.CreditInvoice]: 'Credit invoice',
  [PaymentMethod.InsuranceClaim]: 'Insurance claim',
  [PaymentMethod.KlarnaInstore]: 'Klarna',
  [PaymentMethod.LindorffInvoicing]: 'Lindorff',
  [PaymentMethod.SavFinancing]: 'SAV Financing',
  [PaymentMethod.NordhealthPayments]: 'Nordhealth',
  [PaymentMethod.InternalTransfer]: 'Internal transfer',
  [PaymentMethod.SveaFinancing]: 'SVEA Financing',
  [PaymentMethod.Frakmenta]: 'Frakmenta',
  [PaymentMethod.CustomPayment]: 'Custom payment method',
}

export const factory = Factory.extend<PaymentMethodData>({
  code() {
    return rand(Object.values(PaymentMethod))
  },
  name() {
    const code = this.code as PaymentMethods
    return paymentMethodNames[code]
  },
  display_name() {
    return this.name as string
  },
  is_custom() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  is_card() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  is_active() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  in_prepayments() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  in_invoices() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  in_reports() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  in_end_of_day() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  is_cashdesk_payments() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  maximum_payment_amount() {
    return roundEven(
      toNumber(generateFinanceAmount({ min: 2000, max: 5000 })),
      4,
    )
  },
  cross_department_transfer_enabled() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },

  afterCreate(paymentMethod) {
    paymentMethod.update({
      name: paymentMethodNames[paymentMethod.code as PaymentMethods],
      display_name: paymentMethodNames[paymentMethod.code as PaymentMethods],
    })
  },
})

export function routes(server: MockServer) {
  server.get('/payment_methods', function (this: MirageServer, schema) {
    return schema.all('paymentMethod')
  })
}

export function seeds(server: MockServer) {
  if (server.schema.first('paymentMethod')) {
    return
  }

  Object.values(PaymentMethod).forEach((code) => {
    const isCustomPayment = code === PaymentMethod.CustomPayment

    server.create('paymentMethod', {
      code,
      name: paymentMethodNames[code],
      display_name: isCustomPayment
        ? rand(['Bitcoin', 'Ethereum', 'Apple Pay'])
        : paymentMethodNames[code],
      is_active: true,
      in_invoices: true,
    })
  })
}
